import { useState, useEffect } from 'react';
import { schoolApi } from '@/services/api/schoolApi';

const CACHE_KEY = 'school_logo_url';

// Check if S3 URL is expired
const isUrlExpired = (url: string): boolean => {
  try {
    const expiresMatch = url.match(/Expires=(\d+)/);
    if (!expiresMatch) return true;
    
    const expiresTimestamp = parseInt(expiresMatch[1], 10);
    const now = Math.floor(Date.now() / 1000);
    
    return now >= expiresTimestamp;
  } catch {
    return true;
  }
};

export function useSchoolAssets() {
  const school = process.env.NEXT_PUBLIC_SCHOOL || 'base';
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAndCacheUrl = async () => {
    try {
      const data = await schoolApi.getSchoolAssets();
      const newUrl = data?.logo_url || null;
      
      if (newUrl?.includes('AWSAccessKeyId')) {
        localStorage.setItem(CACHE_KEY, newUrl);
      }
      
      setLogoUrl(newUrl);
    } catch (err) {
      console.error('Failed to fetch logo URL:', err);
      setLogoUrl(null);
      localStorage.removeItem(CACHE_KEY);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadUrl = async () => {
      if (school === 'base' || !school || school === 'null') {
        setLogoUrl('/logo.png');
        setIsLoading(false);
        return;
      }

      // Try to get from cache first
      const cachedUrl = localStorage.getItem(CACHE_KEY);
      if (cachedUrl && !isUrlExpired(cachedUrl)) {
        setLogoUrl(cachedUrl);
        setIsLoading(false);
        return;
      }

      // If no cache or expired, fetch new
      await fetchAndCacheUrl();
    };

    loadUrl();
  }, [school]);

  return {
    logoUrl: school === 'base' || !school || school === 'null' ? '/logo.png' : logoUrl,
    isLoading,
    refreshLogoUrl: fetchAndCacheUrl
  };
}
